import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { IpamAddress, IpamCi, IpamSubnet, IpamSubnetDetail, IpamVlan } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageSection,
  SectionedPage,
  Spinner,
  StatCard,
} from '../components/common'
import { DataTable } from '../components/DataTable'
import { useAsync, useVivo } from '../hooks/useAsync'
import { NotaCache } from '../components/NotaCache'
import { reteNascosta } from '../util/rete'
import { Connessioni } from '../components/Connessioni'
import { ScritturaSpenta, useScrittura } from '../components/ScritturaModulo'
import { t } from '../i18n/lingua'

/**
 * La pagina IPAM: lo spazio IP **documentato** in phpIPAM messo accanto a quello
 * **trovato** dalla discovery.
 *
 * L'IPAM è il piano, la CMDB la realtà. Per ogni subnet si vede quanti indirizzi
 * sono allineati, quanti sono in uso ma nessuno li ha documentati (il prossimo
 * conflitto di indirizzo), quanti sono documentati ma nessuna scansione li ha
 * visti (da liberare, o apparati spenti). Letto in tempo reale — da una sonda o
 * dal Control Plane, come dice la credenziale. The page opens from the copy of
 * the last read (`useVivo` + `NotaCache`); the comparison with the CMDB is
 * computed by the Control Plane at every reply, so it is always current.
 *
 * Se la credenziale di phpIPAM lo consente («Consenti la scrittura»), dal
 * confronto si può anche agire: documentare in phpIPAM gli indirizzi trovati
 * (hostname = nome del CI), allineare il nome di un indirizzo a quello del CI,
 * togliere un indirizzo. I valori li decide il Control Plane dalla CMDB; ogni
 * scrittura va nell'audit con il dettaglio.
 */
export default function Ipam() {
  const servers = useAsync(() => api.ipamServers(), [])
  const [server, setServer] = useState('')
  // From the copy of the last phpIPAM read; «Aggiorna» reads again in the background.
  const subnets = useVivo((refresh) => api.ipamSubnets(server || undefined, refresh), [server])
  const [scelta, setScelta] = useState<IpamSubnet | null>(null)
  const scrittura = useScrittura(
    'ipam',
    (servers.data?.find((x) => x.id === server) ?? servers.data?.[0])?.write_enabled,
  )

  const d = subnets.data
  // Si nascondono le subnet di servizio 127.0.0.0/8 (loopback) e 255.0.0.0/8.
  const righe = (d?.subnets ?? []).filter((s) => !reteNascosta(s.cidr))
  // A subnet chosen from either tab opens its comparison.
  function apri(sub: IpamSubnet | undefined) {
    if (!sub) return
    setScelta(sub)
    window.location.hash = 'confronto'
  }
  const somma = (k: 'documented' | 'aligned' | 'undocumented' | 'not_found') =>
    righe.reduce((n, s) => n + (s.summary?.[k] ?? 0), 0)

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">IPAM</h1>
          <p className="page__subtitle">
            {t('Spazio di indirizzamento IP rilevato da phpIPAM e confrontato con i dati correnti della CMDB. La pagina mostra l’ultima sincronizzazione disponibile e si aggiorna automaticamente in sottofondo, confrontando le allocazioni IP con l’inventario più recente.')}
          </p>
        </div>
        {(servers.data?.length ?? 0) > 1 && (
          <select value={server} onChange={(e) => { setServer(e.target.value); setScelta(null) }}>
            {servers.data!.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </header>

      <SectionedPage
        sections={[
          { id: 'subnet', label: t('Subnet') },
          { id: 'vlan', label: 'VLAN' },
          { id: 'confronto', label: t('Confronto') },
          { id: 'connessioni', label: t('Connessioni') },
        ]}
      >
        <PageSection id="subnet">
          <Card
            title={t('Subnet documentate')}
            actions={
              <button className="secondary" onClick={() => subnets.reload()} disabled={subnets.loading}>
                {subnets.loading ? t('Aggiorno…') : t('Aggiorna')}
              </button>
            }
          >
            <ErrorBanner error={subnets.error} />
            <NotaCache dati={d} />
            {subnets.loading && !d ? (
              <Spinner label={t('Interrogo phpIPAM…')} />
            ) : !d ? null : !d.available ? (
              <p className="muted">{d.reason}</p>
            ) : !righe.length ? (
              <EmptyState title={t('Nessuna subnet')} hint={t('phpIPAM non ha subnet, oppure l’app non ha i permessi di lettura sulle sezioni.')} />
            ) : (
              <>
                <div className="stats">
                  <StatCard label={t('Subnet')} value={righe.length} />
                  <StatCard label={t('Indirizzi documentati')} value={somma('documented')} />
                  <StatCard label={t('Allineati')} value={somma('aligned')} tone="ok" />
                  <StatCard
                    label={t('In uso ma non documentati')}
                    value={somma('undocumented')}
                    tone={somma('undocumented') ? 'warn' : 'ok'}
                  />
                  <StatCard
                    label={t('Documentati ma non trovati')}
                    value={somma('not_found')}
                    tone={somma('not_found') ? 'warn' : 'ok'}
                  />
                </div>
                <DataTable
                  rows={righe}
                  rowKey={(r) => r.id}
                  initialSort={{ key: 'cidr' }}
                  columns={[
                    {
                      key: 'cidr',
                      header: t('Subnet'),
                      mono: true,
                      value: (r) => r.cidr,
                      render: (r) => (
                        <button className="linklike mono" onClick={() => apri(r)}>
                          {r.cidr}
                        </button>
                      ),
                    },
                    { key: 'description', header: t('Descrizione'), value: (r) => r.description },
                    { key: 'section', header: t('Sezione'), value: (r) => r.section },
                    {
                      key: 'vlan',
                      header: 'VLAN',
                      value: (r) => (r.vlan != null ? `${r.vlan} ${r.vlan_name}`.trim() : ''),
                    },
                    { key: 'vrf', header: t('VRF/VPN-INSTANCE'), value: (r) => r.vrf },
                    {
                      key: 'used_percent',
                      header: t('Occupazione'),
                      value: (r) => r.used_percent ?? null,
                      render: (r) => (r.used_percent != null ? `${r.used_percent.toFixed(1)}%` : '—'),
                    },
                    { key: 'documented', header: t('Documentati'), value: (r) => r.summary?.documented ?? 0 },
                    { key: 'aligned', header: t('Allineati'), value: (r) => r.summary?.aligned ?? 0 },
                    {
                      key: 'undocumented',
                      header: t('Non documentati'),
                      value: (r) => r.summary?.undocumented ?? 0,
                      render: (r) => <Conteggio n={r.summary?.undocumented ?? 0} />,
                    },
                    {
                      key: 'not_found',
                      header: t('Non trovati'),
                      value: (r) => r.summary?.not_found ?? 0,
                      render: (r) => <Conteggio n={r.summary?.not_found ?? 0} />,
                    },
                    {
                      key: 'name_mismatch',
                      header: t('Nomi diversi'),
                      value: (r) => r.summary?.name_mismatch ?? 0,
                      render: (r) => <Conteggio n={r.summary?.name_mismatch ?? 0} />,
                    },
                  ]}
                />
              </>
            )}
          </Card>
        </PageSection>

        <PageSection id="vlan">
          <Vlan server={server || undefined} onSubnet={(id) => apri(d?.subnets?.find((x) => x.id === id))} />
        </PageSection>

        <PageSection id="confronto">
          {scelta ? (
            <>
              {scrittura.spentaQui && <ScritturaSpenta />}
              <Confronto subnet={scelta} server={server || undefined} puoScrivere={scrittura.puoScrivere} onCambiato={() => void subnets.rileggiCopia()} />
            </>
          ) : (
            <Card title={t('Confronto')}>
              <p className="muted">{t('Seleziona una subnet per confrontare ogni indirizzo IP documentato in phpIPAM con i rilevamenti della discovery.')}</p>
            </Card>
          )}
        </PageSection>
        <PageSection id="connessioni">
          <Connessioni funzioni={['ipam']} segnaposto="phpipam-sede" />
        </PageSection>
      </SectionedPage>
    </>
  )
}

/**
 * The VLANs documented in phpIPAM: number, name, L2 domain (often the site),
 * description and the subnets phpIPAM puts in each. From the same copy as the
 * subnets; a subnet opens its comparison with the CMDB.
 */
function Vlan({ server, onSubnet }: { server?: string; onSubnet: (id: string) => void }) {
  const vlans = useVivo((refresh) => api.ipamVlans(server, refresh), [server])
  const d = vlans.data
  const righe: IpamVlan[] = d?.vlans ?? []
  const domini = new Set(righe.map((v) => v.domain).filter(Boolean))
  const senzaSubnet = righe.filter((v) => !v.subnets.length).length

  return (
    <Card
      title={t('VLAN documentate')}
      actions={
        <button className="secondary" onClick={() => vlans.reload()} disabled={vlans.loading}>
          {vlans.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={vlans.error} />
      <NotaCache dati={d} />
      {vlans.loading && !d ? (
        <Spinner label={t('Interrogo phpIPAM…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason}</p>
      ) : d.vlans == null ? (
        <Spinner label={t('Leggo le VLAN da phpIPAM…')} />
      ) : !righe.length ? (
        <EmptyState title={t('Nessuna VLAN')} hint={t('phpIPAM non ha VLAN, oppure l’app non ha i permessi di lettura sulle VLAN.')} />
      ) : (
        <>
          <div className="stats">
            <StatCard label="VLAN" value={righe.length} />
            <StatCard label={t('Domini L2')} value={domini.size} />
            <StatCard label={t('Senza subnet')} value={senzaSubnet} />
          </div>
          <DataTable
            rows={righe}
            rowKey={(r) => r.id}
            initialSort={{ key: 'number' }}
            columns={[
              { key: 'number', header: t('Numero'), mono: true, value: (r) => r.number ?? null },
              { key: 'name', header: t('Nome'), value: (r) => r.name },
              {
                key: 'domain',
                header: t('Dominio'),
                value: (r) => r.domain,
                render: (r) =>
                  r.domain ? <span title={r.domain_description || undefined}>{r.domain}</span> : <span className="muted">—</span>,
              },
              { key: 'description', header: t('Descrizione'), value: (r) => r.description },
              {
                key: 'subnets',
                header: t('Subnet'),
                value: (r) => r.subnets.map((s) => s.cidr).join(' '),
                render: (r) =>
                  r.subnets.length ? (
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                      {r.subnets.map((s) => (
                        <button
                          key={s.id}
                          className="linklike mono"
                          title={s.description || undefined}
                          onClick={() => onSubnet(s.id)}
                        >
                          {s.cidr}
                        </button>
                      ))}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  ),
              },
            ]}
          />
        </>
      )}
    </Card>
  )
}

function Conteggio({ n }: { n: number }) {
  return n ? <span className="badge badge--warn">{n}</span> : <span className="muted">0</span>
}

function LinkCi({ ci }: { ci?: IpamCi }) {
  if (!ci) return <span className="muted">—</span>
  return <Link to={`/cis/${ci.ci_id}`}>{ci.name}</Link>
}

function Confronto({ subnet, server, puoScrivere, onCambiato }: {
  subnet: IpamSubnet
  server?: string
  puoScrivere: boolean
  onCambiato?: () => void
}) {
  const dettaglio = useVivo((refresh) => api.ipamSubnet(subnet.id, server, refresh), [subnet.id, server])
  const d: IpamSubnetDetail | null = dettaglio.data
  const [vista, setVista] = useState<'undocumented' | 'not_found' | 'aligned' | 'not_expected'>('undocumented')
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<{ testo: string; problemi: { ip: string; reason: string }[] } | null>(null)
  const [lavoro, setLavoro] = useState('')

  // Ogni azione: un solo lavoro alla volta, esito in chiaro, confronto riletto.
  async function agisci(chiave: string, fare: () => Promise<{ testo: string; problemi?: { ip: string; reason: string }[] }>) {
    setErrore(null)
    setEsito(null)
    setLavoro(chiave)
    try {
      const r = await fare()
      setEsito({ testo: r.testo, problemi: r.problemi ?? [] })
      // The Control Plane reads phpIPAM again in the background after a write:
      // ask for the copy (it says «refreshing»), without starting another read.
      await dettaglio.rileggiCopia()
      onCambiato?.()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro('')
    }
  }

  const documenta = (ips: string[]) =>
    agisci(`doc:${ips.join(',')}`, async () => {
      const r = await api.ipamDocument(subnet.id, ips, server)
      return {
        testo: t('Documentati in phpIPAM: {n} indirizzi.', { n: r.documented.length }),
        problemi: r.errors,
      }
    })

  const allinea = (a: IpamAddress) =>
    agisci(`nome:${a.id}`, async () => {
      const r = await api.ipamAlignName(a.id!, server)
      return { testo: t('Nome di {ip} in phpIPAM: «{prima}» → «{dopo}».', { ip: r.ip, prima: r.before || '—', dopo: r.after }) }
    })

  const togli = (a: IpamAddress) => {
    if (!window.confirm(t('Togliere {ip} ({nome}) da phpIPAM?', { ip: a.ip, nome: a.hostname || '—' }))) return
    void agisci(`via:${a.id}`, async () => {
      const r = await api.ipamDeleteAddress(a.id!, server)
      return { testo: t('Tolto da phpIPAM: {ip}.', { ip: r.ip }) }
    })
  }

  const azioniDoc = puoScrivere && (vista === 'aligned' || vista === 'not_found')
    ? [
        {
          key: 'azioni',
          header: '',
          value: () => '',
          render: (r: IpamAddress) =>
            !r.id ? null : vista === 'aligned' ? (
              r.name_mismatch ? (
                <button className="link" disabled={Boolean(lavoro)} onClick={() => void allinea(r)}>
                  {lavoro === `nome:${r.id}` ? t('Allineo…') : t('Allinea nome')}
                </button>
              ) : null
            ) : (
              <button className="link" disabled={Boolean(lavoro)} onClick={() => togli(r)}>
                {lavoro === `via:${r.id}` ? t('Tolgo…') : t('Togli da phpIPAM')}
              </button>
            ),
        },
      ]
    : []

  const colonneDoc = (conCi: boolean) => [
    { key: 'ip', header: 'IP', mono: true, value: (r: IpamAddress) => r.ip },
    { key: 'hostname', header: t('Nome in phpIPAM'), value: (r: IpamAddress) => r.hostname },
    ...(conCi
      ? [
          {
            key: 'ci',
            header: t('CI trovato'),
            value: (r: IpamAddress) => r.ci?.name ?? '',
            render: (r: IpamAddress) => (
              <>
                <LinkCi ci={r.ci} />
                {r.name_mismatch && <span className="badge badge--warn">{t('nome diverso')}</span>}
              </>
            ),
          },
        ]
      : []),
    { key: 'description', header: t('Descrizione'), value: (r: IpamAddress) => r.description },
    { key: 'owner', header: t('Proprietario'), value: (r: IpamAddress) => r.owner },
    { key: 'tag', header: t('Stato in phpIPAM'), value: (r: IpamAddress) => r.tag },
    { key: 'last_seen', header: t('Ultima vista (phpIPAM)'), value: (r: IpamAddress) => r.last_seen ?? '' },
    ...azioniDoc,
  ]

  return (
    <Card
      title={t('Confronto: {cidr}', { cidr: subnet.cidr })}
      actions={
        <button className="secondary" onClick={() => dettaglio.reload()} disabled={dettaglio.loading}>
          {dettaglio.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <ErrorBanner error={errore ?? dettaglio.error} />
      <NotaCache dati={d} />
      {esito && (
        <div className={`banner ${esito.problemi.length ? 'banner--warn' : 'banner--ok'}`}>
          {esito.testo}
          {esito.problemi.length > 0 && (
            <ul style={{ margin: '6px 0 0' }}>
              {esito.problemi.map((p) => (
                <li key={p.ip}>
                  <span className="mono">{p.ip}</span>: {p.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {dettaglio.loading && !d ? (
        <Spinner label={t('Interrogo phpIPAM…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason}</p>
      ) : (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            {[subnet.description, subnet.section, subnet.vlan != null ? `VLAN ${subnet.vlan}` : '', subnet.vrf ? `VRF ${subnet.vrf}` : '']
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="segmented" role="tablist">
            {(
              [
                ['undocumented', t('In uso ma non documentati'), d.summary?.undocumented],
                ['not_found', t('Documentati ma non trovati'), d.summary?.not_found],
                ['aligned', t('Allineati'), d.summary?.aligned],
                ['not_expected', t('Riservati e DHCP'), d.summary?.not_expected],
              ] as const
            ).map(([k, etichetta, n]) => (
              <button
                key={k}
                role="tab"
                aria-selected={vista === k}
                className={vista === k ? 'segmented__on' : ''}
                onClick={() => setVista(k)}
              >
                {etichetta} ({n ?? 0})
              </button>
            ))}
          </div>
          <p className="field__hint">
            {vista === 'undocumented'
              ? t('Indirizzi che la discovery ha trovato in questa subnet e che phpIPAM non conosce: documentali, o sono il prossimo conflitto di indirizzo.')
              : vista === 'not_found'
                ? t('Indirizzi che phpIPAM dà in uso e che nessuna scansione ha visto: da liberare, oppure apparati spenti o fuori dal perimetro delle scansioni.')
                : vista === 'aligned'
                  ? t('Documentati e trovati. «nome diverso» segnala che phpIPAM e la CMDB chiamano la macchina in due modi.')
                  : t('Indirizzi riservati o del DHCP in phpIPAM: non assegnati a una macchina, non ci si aspetta di trovarli.')}
          </p>
          {vista === 'undocumented' && puoScrivere && (d.undocumented?.length ?? 0) > 0 && (
            <p>
              <button disabled={Boolean(lavoro)} onClick={() => void documenta((d.undocumented ?? []).map((r) => r.ip))}>
                {lavoro.startsWith('doc:') && lavoro.includes(',')
                  ? t('Documento…')
                  : t('Documenta tutti in phpIPAM ({n})', { n: d.undocumented?.length ?? 0 })}
              </button>
            </p>
          )}
          {vista === 'undocumented' ? (
            <DataTable
              rows={(d.undocumented ?? []).filter((r) => !reteNascosta(r.ip))}
              rowKey={(r) => r.ip}
              emptyText={t('Nessuno: tutto ciò che è in uso è documentato.')}
              columns={[
                { key: 'ip', header: 'IP', mono: true, value: (r) => r.ip },
                { key: 'ci', header: t('CI trovato'), value: (r) => r.ci.name, render: (r) => <LinkCi ci={r.ci} /> },
                { key: 'last_seen', header: t('Ultimo rilevamento'), value: (r) => r.ci.last_seen ?? '' },
                ...(puoScrivere
                  ? [
                      {
                        key: 'azioni',
                        header: '',
                        value: () => '',
                        render: (r: { ip: string }) => (
                          <button className="link" disabled={Boolean(lavoro)} onClick={() => void documenta([r.ip])}>
                            {lavoro === `doc:${r.ip}` ? t('Documento…') : t('Documenta')}
                          </button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <DataTable
              rows={((vista === 'not_found' ? d.not_found : vista === 'aligned' ? d.aligned : d.not_expected) ?? []).filter((r) => !reteNascosta(r.ip))}
              rowKey={(r) => r.ip}
              emptyText={t('Nessun indirizzo in questo gruppo.')}
              columns={colonneDoc(vista === 'aligned')}
            />
          )}
        </>
      )}
    </Card>
  )
}
